import React, { useState, useEffect, useRef, forwardRef } from 'react';
import DOMPurify from 'dompurify';
import Draggable from 'react-draggable';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Typography,
  Box,
  Paper,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  FormControlLabel,
} from '@mui/material';
import {
  Tune as PersonalizeIcon,
  RestartAlt as ResetIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Visibility as PreviewIcon,
  Edit as EditIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Code as ValidateIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { constructMinioUrl } from '../../utils/fileUtils';
import ConfirmDialog from '../ConfirmDialog';

// Default visibility settings matching the server schema defaults
const DEFAULT_VISIBILITY = {
  alerts: { owner: true, admin: true, user: true },
  location: { owner: true, admin: true, user: true },
  current_observation: { owner: true, admin: true, user: true },
  latest_image: { owner: true, admin: true, user: true },
  compact_status: { owner: true, admin: true, user: true },
  equipment: {
    select_all: { owner: true, admin: true, user: true },
    mount: { owner: true, admin: true, user: true },
    camera: { owner: true, admin: true, user: true },
    filter_wheel: { owner: true, admin: true, user: true },
    focuser: { owner: true, admin: true, user: true },
    rotator: { owner: true, admin: true, user: true },
    dome: { owner: true, admin: true, user: true },
    weather_station: { owner: true, admin: true, user: true },
    safety_monitor: { owner: true, admin: true, user: true },
    guider: { owner: true, admin: true, user: true },
    switch_hub: { owner: true, admin: true, user: true },
    flat_device: { owner: true, admin: true, user: true },
  },
  nina_options: { owner: true, admin: true, user: false },
  system_status: { owner: true, admin: true, user: true },
  observatory_history: { owner: true, admin: true, user: true },
  weather_history: { owner: false, admin: false, user: false },
  clear_sky_chart: { owner: true, admin: true, user: true },
  navigation_buttons: { owner: true, admin: true, user: true },
  branding: { owner: true, admin: true, user: true },
  live_feeds: { owner: true, admin: true, user: true },
  planetarium_view: { owner: false, admin: false, user: false },
  custom_links: { owner: true, admin: true, user: true },
};

// Section definitions for the visibility table
const TOP_LEVEL_SECTIONS = [
  { key: 'alerts', label: 'Alerts' },
  { key: 'location', label: 'Location' },
  { key: 'current_observation', label: 'Current Observation' },
  { key: 'latest_image', label: 'Latest Image' },
  { key: 'compact_status', label: 'Compact Status' },
  { key: 'nina_options', label: 'NINA Options' },
  { key: 'system_status', label: 'System Status' },
  { key: 'observatory_history', label: 'Observatory History' },
  { key: 'weather_history', label: 'Weather History' },
  { key: 'clear_sky_chart', label: 'Clear Sky Chart' },
  { key: 'navigation_buttons', label: 'Navigation Buttons' },
  { key: 'branding', label: 'Branding' },
  { key: 'live_feeds', label: 'Live Feeds' },
  { key: 'planetarium_view', label: 'Planetarium View' },
  { key: 'custom_links', label: 'Custom Links' },
];

const EQUIPMENT_DEVICES = [
  { key: 'mount', label: 'Mount' },
  { key: 'camera', label: 'Camera' },
  { key: 'filter_wheel', label: 'Filter Wheel' },
  { key: 'focuser', label: 'Focuser' },
  { key: 'rotator', label: 'Rotator' },
  { key: 'dome', label: 'Dome' },
  { key: 'weather_station', label: 'Weather Station' },
  { key: 'safety_monitor', label: 'Safety Monitor' },
  { key: 'guider', label: 'Guider' },
  { key: 'switch_hub', label: 'Switch Hub' },
  { key: 'flat_device', label: 'Flat Device' },
];

const ROLES = ['owner', 'admin', 'user'];
const ROLE_LABELS = { owner: 'Owner', admin: 'Admin', user: 'User' };

// Default section order for the Observatory Detail page
const DEFAULT_SECTION_ORDER = [
  'branding',
  'compact_status',
  'equipment',
  'system_status',
  'observatory_history',
  'clear_sky_chart',
  'latest_image',
  'live_feeds',
  'planetarium_view',
  'navigation_buttons',
];

const SECTION_ORDER_LABELS = {
  branding: 'Observatory Description',
  latest_image: 'Latest Image',
  compact_status: 'Compact Status',
  equipment: 'Equipment Status',
  system_status: 'System Status',
  observatory_history: 'Observatory History',
  clear_sky_chart: 'Clear Sky Chart',
  live_feeds: 'Live Observatory Feed',
  planetarium_view: 'Planetarium View',
  navigation_buttons: 'Navigation Buttons',
};

const DraggablePaper = forwardRef((props, ref) => (
  <Draggable handle="#customize-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
    <Paper ref={ref} {...props} />
  </Draggable>
));

function ObservatoryPersonalizationDialog({ open, onClose, observatory, onSave }) {
  const [activeTab, setActiveTab] = useState(0);
  const [visibility, setVisibility] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoSize, setLogoSize] = useState('small');
  const [savingLogoSize, setSavingLogoSize] = useState(false);
  const [logoLink, setLogoLink] = useState('');
  const [savingLogoLink, setSavingLogoLink] = useState(false);
  const [description, setDescription] = useState('');
  const [savingDescription, setSavingDescription] = useState(false);
  const [clearSkyChartUrl, setClearSkyChartUrl] = useState('');
  const [clearSkyChartImg, setClearSkyChartImg] = useState('');
  const [savingChart, setSavingChart] = useState(false);
  const [liveFeedUrl, setLiveFeedUrl] = useState('');
  const [liveFeedHeight, setLiveFeedHeight] = useState(480);
  const [savingFeed, setSavingFeed] = useState(false);
  const [planetariumProvider, setPlanetariumProvider] = useState('virtualsky');
  const [planetariumOverlay, setPlanetariumOverlay] = useState('reticle');
  const [planetariumMarkerHours, setPlanetariumMarkerHours] = useState(24);
  const [planetariumFov, setPlanetariumFov] = useState(60);
  const [savingPlanetarium, setSavingPlanetarium] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [htmlValidation, setHtmlValidation] = useState(null); // null = not run, { errors: [], warnings: [] }
  const [sectionOrder, setSectionOrder] = useState([...DEFAULT_SECTION_ORDER]);
  const [savingOrder, setSavingOrder] = useState(false);
  const [behavior, setBehavior] = useState({ hide_disconnected_equipment: { owner: false, admin: false, user: false }, latest_image_height: 200 });
  const [savingBehavior, setSavingBehavior] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirm, setConfirm] = useState(null);
  const fileInputRef = useRef(null);
  const prevOpenRef = useRef(false);

  // Initialize all state only when dialog first opens (not on observatory heartbeat updates).
  // Note: don't flip prevOpenRef to true UNTIL init actually proceeds — when the
  // dialog is opened from a route that triggers a fresh observatory fetch (e.g.
  // /admin/observatories/:id?action=customize), `observatory` is null on the
  // first render. Consuming the open-transition here would skip the init
  // permanently and leave visibility=null, which hides the entire dialog (line
  // ~617's `if (!visibility) return null`). Only mark "open consumed" once we
  // have observatory data to init from; reset on close.
  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    if (!justOpened || !observatory) {
      if (!open) prevOpenRef.current = false;
      return;
    }
    prevOpenRef.current = true;

    // Branding state
    setLogoUrl(observatory.branding?.logo_url || null);
    setLogoSize(observatory.branding?.logo_size || 'small');
    setLogoLink(observatory.branding?.logo_link || '');
    setDescription(observatory.branding?.description || '');
    setClearSkyChartUrl(observatory.branding?.clear_sky_chart_url || '');
    setClearSkyChartImg(observatory.branding?.clear_sky_chart_img || '');
    setLiveFeedUrl(observatory.branding?.live_feed_url || '');
    setLiveFeedHeight(observatory.branding?.live_feed_height || 480);
    setPlanetariumProvider(observatory.branding?.planetarium_provider || 'virtualsky');
    setPlanetariumOverlay(observatory.branding?.planetarium_overlay || 'reticle');
    setPlanetariumMarkerHours(observatory.branding?.planetarium_marker_hours || 24);
    setPlanetariumFov(observatory.branding?.planetarium_fov || 60);
    setShowPreview(false);
    setHtmlValidation(null);

    // Section order state - merge saved order with defaults to handle new/removed sections
    const savedOrder = observatory.section_order;
    if (savedOrder && Array.isArray(savedOrder) && savedOrder.length > 0) {
      const validSaved = savedOrder.filter(key => DEFAULT_SECTION_ORDER.includes(key));
      const missing = DEFAULT_SECTION_ORDER.filter(key => !validSaved.includes(key));
      setSectionOrder([...validSaved, ...missing]);
    } else {
      setSectionOrder([...DEFAULT_SECTION_ORDER]);
    }

    // Behavior state
    setBehavior({
      hide_disconnected_equipment: {
        owner: observatory.page_behavior?.hide_disconnected_equipment?.owner || false,
        admin: observatory.page_behavior?.hide_disconnected_equipment?.admin || false,
        user: observatory.page_behavior?.hide_disconnected_equipment?.user || false,
      },
      latest_image_height: observatory.page_behavior?.latest_image_height || 200,
    });

    // Visibility state
    const saved = observatory.page_section_visibility;
    if (saved) {
      const merged = JSON.parse(JSON.stringify(DEFAULT_VISIBILITY));
      for (const key of Object.keys(merged)) {
        if (key === 'equipment') {
          if (saved.equipment) {
            for (const devKey of Object.keys(merged.equipment)) {
              if (saved.equipment[devKey]) {
                merged.equipment[devKey] = { ...merged.equipment[devKey], ...saved.equipment[devKey] };
              }
            }
          }
        } else if (saved[key]) {
          merged[key] = { ...merged[key], ...saved[key] };
        }
      }
      setVisibility(merged);
    } else {
      setVisibility(JSON.parse(JSON.stringify(DEFAULT_VISIBILITY)));
    }
  }, [open, observatory]);

  const handleToggle = (sectionKey, role) => {
    setVisibility(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      updated[sectionKey][role] = !updated[sectionKey][role];
      return updated;
    });
  };

  const handleEquipmentToggle = (deviceKey, role) => {
    setVisibility(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      updated.equipment[deviceKey][role] = !updated.equipment[deviceKey][role];

      // If toggling select_all, apply to all devices
      if (deviceKey === 'select_all') {
        const newValue = updated.equipment.select_all[role];
        for (const device of EQUIPMENT_DEVICES) {
          updated.equipment[device.key][role] = newValue;
        }
      } else {
        // Update select_all based on whether all devices match
        const allChecked = EQUIPMENT_DEVICES.every(d => updated.equipment[d.key][role]);
        updated.equipment.select_all[role] = allChecked;
      }

      return updated;
    });
  };

  const handleResetDefaults = () => {
    setVisibility(JSON.parse(JSON.stringify(DEFAULT_VISIBILITY)));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiService.updateObservatory(observatory._id, {
        page_section_visibility: visibility,
      });
      setSnackbar({ open: true, message: 'Personalization saved successfully', severity: 'success' });
      if (onSave) onSave();
      onClose();
    } catch (error) {
      console.error('Failed to save personalization:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save: ' + (error.response?.data?.error?.message || error.message),
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLogoSize = async (newSize) => {
    setLogoSize(newSize);
    setSavingLogoSize(true);
    try {
      const currentBranding = observatory.branding || {};
      await apiService.updateObservatory(observatory._id, {
        branding: { ...currentBranding, logo_size: newSize },
      });
      setSnackbar({ open: true, message: 'Logo size updated', severity: 'success' });
      if (onSave) onSave();
    } catch (error) {
      console.error('Failed to save logo size:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save size: ' + (error.response?.data?.error?.message || error.message),
        severity: 'error',
      });
    } finally {
      setSavingLogoSize(false);
    }
  };

  const handleSaveLogoLink = async () => {
    setSavingLogoLink(true);
    try {
      const currentBranding = observatory.branding || {};
      await apiService.updateObservatory(observatory._id, {
        branding: { ...currentBranding, logo_link: logoLink || null },
      });
      setSnackbar({ open: true, message: 'Logo link saved', severity: 'success' });
      if (onSave) onSave();
    } catch (error) {
      console.error('Failed to save logo link:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save link: ' + (error.response?.data?.error?.message || error.message),
        severity: 'error',
      });
    } finally {
      setSavingLogoLink(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const result = await apiService.uploadObservatoryLogo(observatory._id, file);
      setLogoUrl(result.data?.logo_url || null);
      setSnackbar({ open: true, message: 'Logo uploaded successfully', severity: 'success' });
      if (onSave) onSave();
    } catch (error) {
      console.error('Failed to upload logo:', error);
      setSnackbar({
        open: true,
        message: 'Failed to upload: ' + (error.response?.data?.error || error.message),
        severity: 'error',
      });
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleLogoDelete = async () => {
    setConfirm({
      title: 'Remove logo?',
      message: 'Are you sure you want to remove the logo?',
      confirmLabel: 'Remove',
      confirmColor: 'error',
      confirmIcon: <DeleteIcon />,
      icon: <DeleteIcon color="error" />,
      onConfirm: async () => {
        try {
          await apiService.deleteObservatoryLogo(observatory._id);
          setLogoUrl(null);
          setSnackbar({ open: true, message: 'Logo removed', severity: 'success' });
          if (onSave) onSave();
        } catch (error) {
          console.error('Failed to delete logo:', error);
          setSnackbar({
            open: true,
            message: 'Failed to remove logo: ' + (error.response?.data?.error || error.message),
            severity: 'error',
          });
          throw error;
        }
      },
    });
  };

  const handleSaveDescription = async () => {
    setSavingDescription(true);
    try {
      // Merge with existing branding to avoid overwriting logo fields
      const currentBranding = observatory.branding || {};
      await apiService.updateObservatory(observatory._id, {
        branding: { ...currentBranding, description },
      });
      setSnackbar({ open: true, message: 'Description saved successfully', severity: 'success' });
      if (onSave) onSave();
    } catch (error) {
      console.error('Failed to save description:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save: ' + (error.response?.data?.error?.message || error.message),
        severity: 'error',
      });
    } finally {
      setSavingDescription(false);
    }
  };

  const handleSaveClearSkyChart = async () => {
    setSavingChart(true);
    try {
      const currentBranding = observatory.branding || {};
      await apiService.updateObservatory(observatory._id, {
        branding: {
          ...currentBranding,
          clear_sky_chart_url: clearSkyChartUrl,
          clear_sky_chart_img: clearSkyChartImg,
        },
      });
      setSnackbar({ open: true, message: 'Clear Sky Chart saved successfully', severity: 'success' });
      if (onSave) onSave();
    } catch (error) {
      console.error('Failed to save clear sky chart:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save: ' + (error.response?.data?.error?.message || error.message),
        severity: 'error',
      });
    } finally {
      setSavingChart(false);
    }
  };

  const handleSaveLiveFeed = async () => {
    setSavingFeed(true);
    try {
      const currentBranding = observatory.branding || {};
      await apiService.updateObservatory(observatory._id, {
        branding: {
          ...currentBranding,
          live_feed_url: liveFeedUrl,
          live_feed_height: liveFeedHeight,
        },
      });
      setSnackbar({ open: true, message: 'Live feed saved successfully', severity: 'success' });
      if (onSave) onSave();
    } catch (error) {
      console.error('Failed to save live feed:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save: ' + (error.response?.data?.error?.message || error.message),
        severity: 'error',
      });
    } finally {
      setSavingFeed(false);
    }
  };

  const handleSavePlanetarium = async () => {
    setSavingPlanetarium(true);
    try {
      const currentBranding = observatory.branding || {};
      await apiService.updateObservatory(observatory._id, {
        branding: {
          ...currentBranding,
          planetarium_provider: planetariumProvider,
          planetarium_overlay: planetariumOverlay,
          planetarium_marker_hours: planetariumMarkerHours,
          planetarium_fov: planetariumFov,
        },
      });
      setSnackbar({ open: true, message: 'Planetarium settings saved successfully', severity: 'success' });
      if (onSave) onSave();
    } catch (error) {
      console.error('Failed to save planetarium settings:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save: ' + (error.response?.data?.error?.message || error.message),
        severity: 'error',
      });
    } finally {
      setSavingPlanetarium(false);
    }
  };

  const validateHtml = (html) => {
    const errors = [];
    const warnings = [];
    if (!html || !html.trim()) {
      setHtmlValidation({ errors: [], warnings: [] });
      return;
    }

    // Check for unclosed quotes in attributes (e.g. <a href="url>)
    // Match tags with attributes, then check each for balanced quotes
    const tagRegex = /<([a-zA-Z][a-zA-Z0-9]*)\s+([^>]*?)>/g;
    let tagMatch;
    while ((tagMatch = tagRegex.exec(html)) !== null) {
      const tagName = tagMatch[1];
      const attrs = tagMatch[2];
      const doubleQuotes = (attrs.match(/"/g) || []).length;
      const singleQuotes = (attrs.match(/'/g) || []).length;
      if (doubleQuotes % 2 !== 0) {
        errors.push(`<${tagName}> tag has an unclosed double-quote in its attributes`);
      }
      if (singleQuotes % 2 !== 0) {
        errors.push(`<${tagName}> tag has an unclosed single-quote in its attributes`);
      }
    }

    // Check for common unclosed tags (not self-closing)
    const voidElements = new Set(['br', 'hr', 'img', 'input', 'meta', 'link', 'col', 'area', 'base', 'embed', 'source', 'track', 'wbr']);
    const optionalCloseElements = new Set(['p', 'li', 'td', 'th', 'tr', 'thead', 'tbody', 'tfoot', 'dt', 'dd']);
    const openTagRegex = /<([a-zA-Z][a-zA-Z0-9]*)\b[^>]*>/g;
    const closeRegex = /<\/([a-zA-Z][a-zA-Z0-9]*)\s*>/g;
    const openCounts = {};
    const closeCounts = {};

    let m;
    while ((m = openTagRegex.exec(html)) !== null) {
      // Skip self-closing tags (e.g. <br />) by checking if match ends with />
      if (m[0].endsWith('/>')) continue;
      const tag = m[1].toLowerCase();
      if (!voidElements.has(tag) && !optionalCloseElements.has(tag)) {
        openCounts[tag] = (openCounts[tag] || 0) + 1;
      }
    }
    while ((m = closeRegex.exec(html)) !== null) {
      const tag = m[1].toLowerCase();
      closeCounts[tag] = (closeCounts[tag] || 0) + 1;
    }

    for (const tag of Object.keys(openCounts)) {
      const opens = openCounts[tag] || 0;
      const closes = closeCounts[tag] || 0;
      if (opens > closes) {
        errors.push(`<${tag}> has ${opens} opening tag${opens > 1 ? 's' : ''} but only ${closes} closing tag${closes !== 1 ? 's' : ''}`);
      }
    }
    for (const tag of Object.keys(closeCounts)) {
      if (!openCounts[tag]) {
        warnings.push(`</${tag}> closing tag without a matching opening tag`);
      }
    }

    // Check for <a> tags missing href
    const anchorRegex = /<a\b([^>]*)>/gi;
    let anchorMatch;
    while ((anchorMatch = anchorRegex.exec(html)) !== null) {
      const attrs = anchorMatch[1];
      if (!/href\s*=/i.test(attrs)) {
        warnings.push('<a> tag is missing an href attribute');
      }
    }

    // Check for <img> tags missing src
    const imgRegex = /<img\b([^>]*)>/gi;
    let imgMatch;
    while ((imgMatch = imgRegex.exec(html)) !== null) {
      const attrs = imgMatch[1];
      if (!/src\s*=/i.test(attrs)) {
        warnings.push('<img> tag is missing a src attribute');
      }
    }

    setHtmlValidation({ errors, warnings });
  };

  const handleMoveSection = (index, direction) => {
    const newOrder = [...sectionOrder];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newOrder.length) return;
    [newOrder[index], newOrder[targetIndex]] = [newOrder[targetIndex], newOrder[index]];
    setSectionOrder(newOrder);
  };

  const handleResetOrder = () => {
    setSectionOrder([...DEFAULT_SECTION_ORDER]);
  };

  const handleSaveOrder = async () => {
    setSavingOrder(true);
    try {
      await apiService.updateObservatory(observatory._id, {
        section_order: sectionOrder,
      });
      setSnackbar({ open: true, message: 'Section order saved successfully', severity: 'success' });
      if (onSave) onSave();
    } catch (error) {
      console.error('Failed to save section order:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save: ' + (error.response?.data?.error?.message || error.message),
        severity: 'error',
      });
    } finally {
      setSavingOrder(false);
    }
  };

  const handleSaveBehavior = async () => {
    setSavingBehavior(true);
    try {
      await apiService.updateObservatory(observatory._id, {
        page_behavior: behavior,
      });
      setSnackbar({ open: true, message: 'Behavior settings saved successfully', severity: 'success' });
      if (onSave) onSave();
    } catch (error) {
      console.error('Failed to save behavior:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save: ' + (error.response?.data?.error?.message || error.message),
        severity: 'error',
      });
    } finally {
      setSavingBehavior(false);
    }
  };

  if (!visibility) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperComponent={DraggablePaper}
        aria-labelledby="customize-dialog-title"
      >
        <DialogTitle id="customize-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'move' }}>
          <PersonalizeIcon />
          <Typography variant="h6">Customize Observatory</Typography>
        </DialogTitle>

        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={<>Section<br/>Visibility</>} />
          <Tab label={<>Section<br/>Order</>} />
          <Tab label="Behavior" />
          <Tab label="Logo" />
          <Tab label="Description" />
          <Tab label={<>Clear Sky<br/>Chart</>} />
          <Tab label="Live Feed" />
          <Tab label="Planetarium" />
        </Tabs>

        <DialogContent dividers sx={{ minHeight: 400 }}>
          {/* Tab 0: Section Visibility */}
          {activeTab === 0 && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Control which sections are visible to each role. Uncheck a box to hide that section for the corresponding role.
              </Typography>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', width: '50%' }}>Section</TableCell>
                      {ROLES.map(role => (
                        <TableCell key={role} align="center" sx={{ fontWeight: 'bold' }}>
                          {ROLE_LABELS[role]}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Top-level sections before Equipment */}
                    {TOP_LEVEL_SECTIONS.filter(s => ['alerts', 'location', 'current_observation', 'latest_image', 'compact_status'].includes(s.key)).map(section => (
                      <TableRow key={section.key}>
                        <TableCell>{section.label}</TableCell>
                        {ROLES.map(role => (
                          <TableCell key={role} align="center" padding="checkbox">
                            <Checkbox
                              checked={visibility[section.key]?.[role] !== false}
                              onChange={() => handleToggle(section.key, role)}
                              size="small"
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}

                    {/* Equipment - Select All */}
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Equipment (Select All)</TableCell>
                      {ROLES.map(role => (
                        <TableCell key={role} align="center" padding="checkbox">
                          <Checkbox
                            checked={visibility.equipment?.select_all?.[role] !== false}
                            onChange={() => handleEquipmentToggle('select_all', role)}
                            size="small"
                            indeterminate={
                              !EQUIPMENT_DEVICES.every(d => visibility.equipment?.[d.key]?.[role] !== false) &&
                              EQUIPMENT_DEVICES.some(d => visibility.equipment?.[d.key]?.[role] !== false)
                            }
                          />
                        </TableCell>
                      ))}
                    </TableRow>

                    {/* Individual equipment devices */}
                    {EQUIPMENT_DEVICES.map(device => (
                      <TableRow key={device.key}>
                        <TableCell sx={{ pl: 4 }}>{device.label}</TableCell>
                        {ROLES.map(role => (
                          <TableCell key={role} align="center" padding="checkbox">
                            <Checkbox
                              checked={visibility.equipment?.[device.key]?.[role] !== false}
                              onChange={() => handleEquipmentToggle(device.key, role)}
                              size="small"
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}

                    {/* Remaining top-level sections after Equipment */}
                    {TOP_LEVEL_SECTIONS.filter(s => !['alerts', 'location', 'current_observation', 'latest_image', 'compact_status'].includes(s.key)).map(section => (
                      <TableRow key={section.key}>
                        <TableCell>{section.label}</TableCell>
                        {ROLES.map(role => (
                          <TableCell key={role} align="center" padding="checkbox">
                            <Checkbox
                              checked={visibility[section.key]?.[role] !== false}
                              onChange={() => handleToggle(section.key, role)}
                              size="small"
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {/* Tab 1: Section Order */}
          {activeTab === 1 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Use the arrow buttons to reorder sections on the Observatory Detail page. Changes take effect after saving.
              </Typography>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', width: '10%' }} align="center">#</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '60%' }}>Section</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '30%' }} align="center">Move</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sectionOrder.map((key, index) => (
                      <TableRow key={key} hover>
                        <TableCell align="center">
                          <Typography variant="body2" color="text.secondary">{index + 1}</Typography>
                        </TableCell>
                        <TableCell>{SECTION_ORDER_LABELS[key] || key}</TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleMoveSection(index, -1)}
                            disabled={index === 0}
                          >
                            <ArrowUpIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleMoveSection(index, 1)}
                            disabled={index === sectionOrder.length - 1}
                          >
                            <ArrowDownIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Tab 2: Behavior */}
          {activeTab === 2 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Configure display behavior for the Observatory Detail page.
              </Typography>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Hide Disconnected Equipment</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                When checked, equipment cards and status summary items for disconnected equipment will be hidden for that role.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                {['owner', 'admin', 'user'].map(role => (
                  <FormControlLabel
                    key={role}
                    control={
                      <Checkbox
                        checked={behavior.hide_disconnected_equipment?.[role] || false}
                        onChange={(e) => setBehavior(prev => ({
                          ...prev,
                          hide_disconnected_equipment: { ...prev.hide_disconnected_equipment, [role]: e.target.checked },
                        }))}
                      />
                    }
                    label={role.charAt(0).toUpperCase() + role.slice(1)}
                  />
                ))}
              </Box>

              <Typography variant="subtitle2" sx={{ mb: 1 }}>Latest Image Preview Height</Typography>
              <TextField
                type="number"
                value={behavior.latest_image_height}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  if (!isNaN(val)) setBehavior(prev => ({ ...prev, latest_image_height: Math.max(100, Math.min(800, val)) }));
                }}
                inputProps={{ min: 100, max: 800, step: 10 }}
                sx={{ width: 160 }}
                size="small"
                helperText="100–800px (default: 200)"
              />
            </Box>
          )}

          {/* Tab 3: Logo */}
          {activeTab === 3 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Upload a logo for your observatory. It will be displayed on the Observatory Detail page header. Supported formats: JPEG, PNG, WebP, SVG. Max size: 5MB.
              </Typography>

              {/* Current logo preview */}
              {logoUrl && (
                <Box sx={{ mb: 3, textAlign: 'center' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Current Logo</Typography>
                  <Box sx={{
                    display: 'inline-block',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 2,
                    bgcolor: 'background.default',
                  }}>
                    <img
                      src={constructMinioUrl(logoUrl)}
                      alt="Observatory logo"
                      style={{ maxWidth: 200, maxHeight: 200, objectFit: 'contain' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    <Button
                      startIcon={<DeleteIcon />}
                      color="error"
                      size="small"
                      onClick={handleLogoDelete}
                    >
                      Remove Logo
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Upload area */}
              <Box sx={{ textAlign: 'center' }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/svg+xml"
                  style={{ display: 'none' }}
                  onChange={handleLogoUpload}
                />
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  size="large"
                >
                  {uploading ? 'Uploading...' : (logoUrl ? 'Replace Logo' : 'Upload Logo')}
                </Button>
              </Box>

              {/* Logo size selector */}
              {logoUrl && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Display Size</Typography>
                  <ToggleButtonGroup
                    value={logoSize}
                    exclusive
                    onChange={(_, newSize) => { if (newSize) handleSaveLogoSize(newSize); }}
                    disabled={savingLogoSize}
                    size="small"
                  >
                    <ToggleButton value="small">Small (56px)</ToggleButton>
                    <ToggleButton value="medium">Medium (80px)</ToggleButton>
                    <ToggleButton value="large">Large (120px)</ToggleButton>
                    <ToggleButton value="xlarge">Extra Large (160px)</ToggleButton>
                    <ToggleButton value="huge">Huge (200px)</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              )}

              {/* Logo link URL */}
              {logoUrl && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Logo Link URL</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={logoLink}
                    onChange={(e) => setLogoLink(e.target.value)}
                    placeholder="https://..."
                    helperText="Clicking the logo will open this URL in a new tab"
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleSaveLogoLink}
                    disabled={savingLogoLink}
                    sx={{ mt: 1 }}
                  >
                    {savingLogoLink ? 'Saving...' : 'Save Link'}
                  </Button>
                </Box>
              )}
            </Box>
          )}

          {/* Tab 4: Description */}
          {activeTab === 4 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Add a custom description for your observatory. HTML is supported for rich formatting. Max 5000 characters.
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button
                  size="small"
                  variant={!showPreview ? 'contained' : 'outlined'}
                  startIcon={<EditIcon />}
                  onClick={() => setShowPreview(false)}
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  variant={showPreview ? 'contained' : 'outlined'}
                  startIcon={<PreviewIcon />}
                  onClick={() => setShowPreview(true)}
                >
                  Preview
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ValidateIcon />}
                  onClick={() => validateHtml(description)}
                  color={htmlValidation?.errors?.length ? 'error' : htmlValidation ? 'success' : 'inherit'}
                >
                  Validate HTML
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', ml: 1 }}>
                  {description.length} / 5000
                </Typography>
              </Box>

              {htmlValidation && (
                <Box sx={{ mb: 2 }}>
                  {htmlValidation.errors.length === 0 && htmlValidation.warnings.length === 0 ? (
                    <Alert severity="success" sx={{ py: 0.5 }}>
                      No HTML issues found.
                    </Alert>
                  ) : (
                    <>
                      {htmlValidation.errors.map((err, i) => (
                        <Alert key={`err-${i}`} severity="error" sx={{ py: 0.5, mb: 0.5 }}>
                          {err}
                        </Alert>
                      ))}
                      {htmlValidation.warnings.map((warn, i) => (
                        <Alert key={`warn-${i}`} severity="warning" sx={{ py: 0.5, mb: 0.5 }}>
                          {warn}
                        </Alert>
                      ))}
                    </>
                  )}
                </Box>
              )}

              {!showPreview ? (
                <TextField
                  multiline
                  rows={12}
                  fullWidth
                  value={description}
                  onChange={(e) => { setDescription(e.target.value.slice(0, 5000)); setHtmlValidation(null); }}
                  placeholder="Enter your observatory description here. HTML tags are supported for formatting (e.g. <b>bold</b>, <i>italic</i>, <a href='...'>links</a>, <br> line breaks)."
                  variant="outlined"
                  sx={{ fontFamily: 'monospace' }}
                  inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.875rem' } }}
                />
              ) : (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    minHeight: 200,
                    bgcolor: 'background.default',
                  }}
                >
                  {description ? (
                    <Box
                      sx={{
                        '& a': { color: 'primary.main' },
                        '& img': { maxWidth: '100%' },
                      }}
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No description entered yet.
                    </Typography>
                  )}
                </Paper>
              )}
            </Box>
          )}

          {/* Tab 5: Clear Sky Chart */}
          {activeTab === 5 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, '& a': { color: 'primary.main' } }}>
                Link your observatory's Clear Dark Sky chart. Find the location that is closest to your observatory at{' '}
                <a href="https://www.cleardarksky.com/csk/" target="_blank" rel="noopener noreferrer">cleardarksky.com</a>{' '}
                and copy the link URL and image URL from the embed code.
              </Typography>

              <TextField
                label="Chart Link URL"
                fullWidth
                value={clearSkyChartUrl}
                onChange={(e) => {
                  let val = e.target.value;
                  const hrefMatch = val.match(/href=["']([^"']+)["']/i);
                  if (hrefMatch) val = hrefMatch[1];
                  setClearSkyChartUrl(val);
                }}
                placeholder="https://www.cleardarksky.com/c/YourLocationkey.html"
                sx={{ mb: 2 }}
                size="small"
              />
              <TextField
                label="Chart Image URL"
                fullWidth
                value={clearSkyChartImg}
                onChange={(e) => {
                  let val = e.target.value;
                  const srcMatch = val.match(/src=["']([^"']+)["']/i);
                  if (srcMatch) val = srcMatch[1];
                  setClearSkyChartImg(val);
                }}
                placeholder="https://www.cleardarksky.com/c/YourLocationcsk.gif?c=..."
                sx={{ mb: 3 }}
                size="small"
              />

              {/* Preview */}
              {clearSkyChartImg && (
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Preview</Typography>
                  <a href={clearSkyChartUrl || '#'} target="_blank" rel="noopener noreferrer">
                    <img
                      src={clearSkyChartImg}
                      alt="Clear Sky Chart"
                      style={{ maxWidth: '100%' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </a>
                </Box>
              )}
            </Box>
          )}

          {/* Tab 6: Live Feed */}
          {activeTab === 6 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Embed a live camera or webcam feed on your observatory detail page. Enter the URL of a webcam page,
                YouTube live stream embed, or any embeddable content. The feed will be displayed in an iframe.
              </Typography>

              <TextField
                label="Live Feed URL"
                fullWidth
                value={liveFeedUrl}
                onChange={(e) => setLiveFeedUrl(e.target.value)}
                placeholder="https://www.youtube.com/embed/your-stream-id"
                sx={{ mb: 2 }}
                size="small"
              />

              <TextField
                label="Feed Height (px)"
                type="number"
                value={liveFeedHeight}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  if (!isNaN(val)) setLiveFeedHeight(Math.max(100, Math.min(2000, val)));
                }}
                inputProps={{ min: 100, max: 2000, step: 10 }}
                sx={{ mb: 3, width: 160 }}
                size="small"
                helperText="100–2000"
              />

              {/* Preview */}
              {liveFeedUrl && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Preview</Typography>
                  <Paper sx={{ p: 0, overflow: 'hidden' }} elevation={2}>
                    <iframe
                      src={liveFeedUrl}
                      title="Live Feed Preview"
                      style={{ width: '100%', height: `${liveFeedHeight}px`, border: 'none' }}
                      allowFullScreen
                    />
                  </Paper>
                </Box>
              )}
            </Box>
          )}

          {/* Tab 7: Planetarium */}
          {activeTab === 7 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Choose which planetarium engine to embed on your observatory detail page.
                The view is auto-configured from your observatory's location coordinates.
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                {[
                  { value: 'stellarium', label: 'Stellarium Web', desc: 'Full-featured planetarium with realistic sky rendering' },
                  { value: 'virtualsky', label: 'VirtualSky', desc: 'Lightweight embed with constellation lines and labels' },
                ].map(opt => (
                  <Paper
                    key={opt.value}
                    onClick={() => setPlanetariumProvider(opt.value)}
                    sx={{
                      flex: 1, p: 2, cursor: 'pointer',
                      border: 2,
                      borderColor: planetariumProvider === opt.value ? 'primary.main' : 'divider',
                      bgcolor: planetariumProvider === opt.value ? 'action.selected' : 'background.paper',
                      '&:hover': { borderColor: 'primary.light' },
                    }}
                    elevation={planetariumProvider === opt.value ? 3 : 1}
                  >
                    <Typography variant="subtitle1" fontWeight={600}>{opt.label}</Typography>
                    <Typography variant="body2" color="text.secondary">{opt.desc}</Typography>
                  </Paper>
                ))}
              </Box>

              <Typography variant="subtitle2" sx={{ mb: 1 }}>Mount Overlay</Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                {[
                  { value: 'reticle', label: 'Reticle', desc: 'Crosshair showing mount pointing position' },
                  { value: 'fov', label: 'Camera FOV', desc: 'Field of view rectangle with dimensions' },
                  { value: 'none', label: 'None', desc: 'No overlay on the planetarium view' },
                ].map(opt => (
                  <Paper
                    key={opt.value}
                    onClick={() => setPlanetariumOverlay(opt.value)}
                    sx={{
                      flex: 1, p: 2, cursor: 'pointer',
                      border: 2,
                      borderColor: planetariumOverlay === opt.value ? 'primary.main' : 'divider',
                      bgcolor: planetariumOverlay === opt.value ? 'action.selected' : 'background.paper',
                      '&:hover': { borderColor: 'primary.light' },
                    }}
                    elevation={planetariumOverlay === opt.value ? 3 : 1}
                  >
                    <Typography variant="subtitle1" fontWeight={600}>{opt.label}</Typography>
                    <Typography variant="body2" color="text.secondary">{opt.desc}</Typography>
                  </Paper>
                ))}
              </Box>

              {planetariumProvider === 'virtualsky' && (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Sky Map Field of View</Typography>
                  <TextField
                    label="Default field of view (degrees)"
                    type="number"
                    value={planetariumFov}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      if (!isNaN(val)) setPlanetariumFov(Math.max(10, Math.min(180, val)));
                    }}
                    inputProps={{ min: 10, max: 180, step: 5 }}
                    sx={{ width: 300, mb: 3 }}
                    size="small"
                    helperText="10-180 degrees (default: 60). Smaller values zoom in, larger values show more sky."
                  />

                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Observation Markers</Typography>
                  <TextField
                    label="Show observations from last N hours"
                    type="number"
                    value={planetariumMarkerHours}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      if (!isNaN(val)) setPlanetariumMarkerHours(Math.max(1, Math.min(720, val)));
                    }}
                    inputProps={{ min: 1, max: 720, step: 1 }}
                    sx={{ width: 300, mb: 3 }}
                    size="small"
                    helperText="1-720 hours (default: 24). Recent observations appear as coloured markers on the sky."
                  />
                </>
              )}

              {/* Preview */}
              {observatory.location?.latitude != null && observatory.location?.longitude != null && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Preview</Typography>
                  <Paper sx={{ p: 0, overflow: 'hidden' }} elevation={2}>
                    <iframe
                      src={planetariumProvider === 'virtualsky'
                        ? `/virtualsky/index.html?longitude=${observatory.location.longitude}&latitude=${observatory.location.latitude}&projection=gnomic&showstarlabels=true&live=true&fov=${planetariumFov}`
                        : `https://stellarium-web.org/?lat=${observatory.location.latitude}&lng=${observatory.location.longitude}`
                      }
                      title="Planetarium Preview"
                      style={{ width: '100%', height: '400px', border: 'none' }}
                      allowFullScreen
                    />
                  </Paper>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
          {activeTab === 0 ? (
            <Button
              startIcon={<ResetIcon />}
              onClick={handleResetDefaults}
              color="warning"
            >
              Reset to Defaults
            </Button>
          ) : activeTab === 1 ? (
            <Button
              startIcon={<ResetIcon />}
              onClick={handleResetOrder}
              color="warning"
            >
              Reset to Default Order
            </Button>
          ) : activeTab === 2 ? (
            <Button
              startIcon={<ResetIcon />}
              onClick={() => setBehavior({
                hide_disconnected_equipment: { owner: false, admin: false, user: false },
                latest_image_height: 200,
              })}
              color="warning"
            >
              Reset to Defaults
            </Button>
          ) : <Box />}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onClose}>Close</Button>
            {activeTab === 0 && (
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            )}
            {activeTab === 1 && (
              <Button
                variant="contained"
                onClick={handleSaveOrder}
                disabled={savingOrder}
              >
                {savingOrder ? 'Saving...' : 'Save Order'}
              </Button>
            )}
            {activeTab === 2 && (
              <Button
                variant="contained"
                onClick={handleSaveBehavior}
                disabled={savingBehavior}
              >
                {savingBehavior ? 'Saving...' : 'Save Behavior'}
              </Button>
            )}
            {activeTab === 4 && (
              <Button
                variant="contained"
                onClick={handleSaveDescription}
                disabled={savingDescription}
              >
                {savingDescription ? 'Saving...' : 'Save Description'}
              </Button>
            )}
            {activeTab === 5 && (
              <Button
                variant="contained"
                onClick={handleSaveClearSkyChart}
                disabled={savingChart}
              >
                {savingChart ? 'Saving...' : 'Save Chart'}
              </Button>
            )}
            {activeTab === 6 && (
              <Button
                variant="contained"
                onClick={handleSaveLiveFeed}
                disabled={savingFeed}
              >
                {savingFeed ? 'Saving...' : 'Save Feed'}
              </Button>
            )}
            {activeTab === 7 && (
              <Button
                variant="contained"
                onClick={handleSavePlanetarium}
                disabled={savingPlanetarium}
              >
                {savingPlanetarium ? 'Saving...' : 'Save Planetarium'}
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        confirmColor={confirm?.confirmColor}
        confirmIcon={confirm?.confirmIcon}
        icon={confirm?.icon}
        onConfirm={confirm?.onConfirm}
        onClose={() => setConfirm(null)}
      />
    </>
  );
}

export default ObservatoryPersonalizationDialog;
